/** The public map: every circuit the community has surveyed.
 *
 * This is the page that has to argue for the whole project to somebody who
 * has not signed in and may never, so it is deliberately a scoreboard rather
 * than a directory. The header says how much of the game's roster exists,
 * the cards say who built each piece of it and how complete it is, and the
 * footer says where the geometry actually lives — in a public repository,
 * filed as pull requests anybody can read. Nothing on the screen requires an
 * account and nothing on it is hidden from one.
 *
 * The whole list arrives in a single cached request and every control works
 * on it in the browser. That is a deliberate choice for a hundred and
 * twenty-odd rows: the filter box responds to a keystroke without a round
 * trip, the segmented control never leaves the page half-filtered, and a
 * reader on a slow connection pays for one document instead of one per
 * opinion. The arithmetic behind it lives in `components/tracks/list.ts`,
 * where it can be tested without a browser.
 *
 * A failed fetch shows what the service said and offers to try again; an
 * empty map and an over-narrow filter are different states and say different
 * things, because "no results" that could mean either is a bug report waiting
 * to be filed.
 *
 * The outlines come second. The list carries facts and no geometry, so every
 * card first wears the placeholder loop; once the list is in, the page asks
 * for each published circuit's outline — borders and finish line, a sixth of
 * the full document — a few at a time, and a card becomes a real map as its
 * answer lands. `components/tracks/outlines.ts` does the asking and is
 * tested on its own.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { getMeta, getTrackOutline, listTracks } from '@/lib/api';
import { count } from '@/lib/format';
import type { ServiceMeta, Track, TrackGeometry, TrackStats } from '@/lib/types';
import { EmptyState, ErrorNote, LoadingPanel, Page, Segmented, StatTile } from '@/components/ui';
import { TrackCard } from '@/components/tracks/TrackCard';
import {
  GT7_CIRCUITS,
  TRACK_FILTERS,
  TRACK_PAGE_SIZE,
  deriveTrackStats,
  visibleTracks,
} from '@/components/tracks/list';
import type { TrackFilter } from '@/components/tracks/list';
import { fetchOutlines, wantsOutline } from '@/components/tracks/outlines';

/** Where the published geometry lives when `/api/meta` cannot be reached.
 *  The page still has to name the repository in its footer. */
const TRACK_DATA_URL = 'https://github.com/jbhoorasingh/gt7-datalogger-track-data';

function HeaderStats({ stats }: { stats: TrackStats }) {
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
      <StatTile align="right" value={count(stats.published)} label="published" />
      <StatTile align="right" value={count(stats.pending)} label="pending uploads" />
      <StatTile align="right" value={count(stats.contributors)} label="contributors" />
      <StatTile
        align="right"
        tone="muted"
        value={count(stats.unsurveyed)}
        label={`of ${count(stats.total_circuits)} unsurveyed`}
      />
    </div>
  );
}

export default function Tracks() {
  const [tracks, setTracks] = useState<Track[] | null>(null);
  /** Each published circuit's drawing as it arrives; null once asked and
   *  missing, so the card keeps its placeholder rather than asking again. */
  const [outlines, setOutlines] = useState<Record<string, TrackGeometry | null>>({});
  const [serverStats, setServerStats] = useState<TrackStats | null>(null);
  const [meta, setMeta] = useState<ServiceMeta | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<TrackFilter>('all');
  const filterId = useId();

  useEffect(() => {
    let live = true;
    setLoading(true);

    void (async () => {
      try {
        const reply = await listTracks({ limit: TRACK_PAGE_SIZE });
        if (!live) return;
        setTracks(reply.tracks ?? []);
        setServerStats(reply.stats ?? null);
        setError(null);
      } catch (thrown) {
        if (!live) return;
        setTracks(null);
        setServerStats(null);
        setError(thrown);
      } finally {
        if (live) setLoading(false);
      }
    })();

    return () => {
      live = false;
    };
  }, [attempt]);

  // The drawings, once the list is in: only the published circuits, a few
  // at a time, and nothing lands on a screen that has since moved on.
  useEffect(() => {
    if (!tracks) return undefined;
    let live = true;
    const wanted = tracks.filter(wantsOutline).map((track) => track.official_id);
    void fetchOutlines(
      wanted,
      async (id) => (await getTrackOutline(id)).geometry,
      (id, outline) => {
        if (live) setOutlines((previous) => ({ ...previous, [id]: outline }));
      },
    );
    return () => {
      live = false;
    };
  }, [tracks]);

  // The deployment's own links are chrome, not content: if this call fails
  // the footer falls back to the repository this service was built for
  // rather than putting an error on a page that otherwise loaded.
  useEffect(() => {
    let live = true;
    void getMeta().then(
      (value) => {
        if (live) setMeta(value);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  const stats = useMemo(
    () => serverStats ?? deriveTrackStats(tracks ?? []),
    [serverStats, tracks],
  );
  const visible = useMemo(
    () => visibleTracks(tracks ?? [], query, filter),
    [tracks, query, filter],
  );

  const filtered = query.trim() !== '' || filter !== 'all';
  const trackDataUrl = meta?.track_data_url || TRACK_DATA_URL;
  const trackDataName = meta?.track_data_repo.split('/')[1] ?? 'gt7-datalogger-track-data';

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ fontSize: 24, margin: 0 }}>Tracks</h1>
          <div
            style={{
              fontSize: 13,
              color: 'var(--color-neutral-500)',
              textWrap: 'pretty',
            }}
          >
            Every circuit the community has surveyed. Public — what’s published, what’s waiting,
            and how many people it took.
          </div>
        </div>
        <HeaderStats stats={stats} />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor={filterId} className="sr-only">
          Filter circuits by name
        </label>
        <input
          id={filterId}
          className="input"
          type="search"
          value={query}
          placeholder="Filter by name…"
          style={{ maxWidth: 260 }}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Segmented
          label="Filter by status"
          options={TRACK_FILTERS}
          value={filter}
          onChange={setFilter}
        />
        <span
          style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--color-neutral-600)' }}
        >
          sorted by last activity
        </span>
      </div>

      {error ? (
        <ErrorNote
          error={error}
          title="The circuits could not be loaded."
          onRetry={() => setAttempt((value) => value + 1)}
        />
      ) : null}

      {loading && tracks === null ? <LoadingPanel label="loading circuits" /> : null}

      {tracks !== null && visible.length > 0 ? (
        <>
          <p role="status" className="sr-only">
            {count(visible.length)} of {count(tracks.length)} circuits shown
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {visible.map((track) => (
              <TrackCard
                key={track.official_id}
                track={{ ...track, geometry: outlines[track.official_id] ?? null }}
              />
            ))}
          </div>
        </>
      ) : null}

      {tracks !== null && visible.length === 0 ? (
        filtered ? (
          <EmptyState
            title="No circuit matches that"
            action={
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                }}
              >
                Clear the filter
              </button>
            }
          >
            {count(tracks.length)} circuits have been surveyed so far, but none of them match the
            name and status you asked for.
          </EmptyState>
        ) : (
          <EmptyState title="Nothing has been surveyed yet">
            All {count(GT7_CIRCUITS)} circuits are still unmapped. Drive one in the datalogger with
            Tracks sync switched on and it will appear here once the merge job has published it.
          </EmptyState>
        )
      ) : null}

      <p style={{ fontSize: 11.5, color: 'var(--color-neutral-600)', margin: 0, textWrap: 'pretty' }}>
        Published geometry lives in{' '}
        <a href={trackDataUrl} target="_blank" rel="noreferrer noopener">
          {trackDataName}
        </a>
        ; this page reads that repository plus the service’s pending counts. Survey a circuit in the
        datalogger and turn on Tracks sync to add one.
      </p>
    </Page>
  );
}
